import { Code, ConnectError } from "@connectrpc/connect";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { type FC, forwardRef, memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { creatingEnvironmentForProjectAtom } from "@/atoms/ui-state";
import { Button, type ButtonProps } from "@/components/flexkit/Button";
import { useToast } from "@/components/flexkit/toast";
import { SelectEnvironmentClassForProjectModal } from "@/components/projects/SelectEnvironmentClassForProjectModal";
import { Tooltip } from "@/components/Tooltip";
import { useCreateEnvironmentFromProjectWithAuth } from "@/hooks/use-create-environment-from-project";
import { type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useGetPreferredEnvironmentClassPerProject } from "@/hooks/use-preferred-env-class-per-project";
import { TrackLocations } from "@/hooks/use-segment";
import {
    type CheckScmAuthForProjectResult,
    getRepoUrlFromInitializer,
    type PlainRunnerEnvironmentClass,
} from "@/queries/environment-queries";
import {
    useListProjectEnvironmentClasses,
    useListProjectEnvironmentClassesOptions,
    type PlainProject,
} from "@/queries/project-queries";
import type { PlainAuthenticationMethodPersonalAccessToken } from "@/queries/runner-queries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError, isCreditsExhaustionError, isPolicyError, isVerifyCardError } from "@/utils/errors";
import { CreateEnvironmentFromProjectBadRequestEnvironmentClassDetailsSchema } from "gitpod-next-api/gitpod/v1/environment_pb";

/**
 * Returns true when the environment class selector should be shown —
 * i.e. the project has multiple classes and no preferred class is set.
 */
function needsEnvClassSelector(
    projectId: string | undefined,
    preferredEnvironmentClassPerProject: Map<string, string> | undefined,
    environmentClasses: { environmentClasses?: unknown[] } | undefined,
): boolean {
    return (
        !!projectId &&
        !preferredEnvironmentClassPerProject?.get(projectId) &&
        !!environmentClasses?.environmentClasses &&
        environmentClasses.environmentClasses.length > 1
    );
}

type Props = {
    project?: PlainProject;
    onCreateSuccess?: () => void;
    hideTooltip?: boolean;
    showProjectEnvClassSelector?: boolean;
    /**
     * When true, defers fetching project environment classes until the user
     * clicks the button. Prevents N+1 queries when many buttons are rendered
     * (e.g. in the sidebar).
     */
    deferClassesFetch?: boolean;
} & ButtonProps;

export const CreateEnvironmentButton = memo(
    forwardRef<HTMLButtonElement, Props>(
        ({ project, onCreateSuccess, hideTooltip, showProjectEnvClassSelector, deferClassesFetch, ...props }, ref) => {
            const { toast } = useToast();
            const navigate = useNavigate();
            const queryClient = useQueryClient();
            const setCreating = useSetAtom(creatingEnvironmentForProjectAtom(project?.id ?? ""));

            const { preferredEnvironmentClassPerProject, isPending: isPendingPreference } =
                useGetPreferredEnvironmentClassPerProject();
            const classesQueryOptions = useListProjectEnvironmentClassesOptions(project?.id, !deferClassesFetch);
            const { data: projectEnvironmentClasses, isLoading: isLoadingProjectClasses } =
                useListProjectEnvironmentClasses(project?.id, !deferClassesFetch);

            const [isFetchingClasses, setIsFetchingClasses] = useState(false);
            const [showScmAuthModal, setShowScmAuthModal] = useState<
                (CheckScmAuthForProjectResult & { rememberChoice?: boolean }) | undefined
            >();
            const [showEnvClassSelectorModal, setShowEnvClassSelectorModal] = useState<
                { filteredEnvironmentClassIds?: string[]; title?: string; description?: string } | undefined
            >();

            const { handleCreateEnvironmentFromProject: createEnvironmentFromProjectWithAuth, isLoading: isCreating } =
                useCreateEnvironmentFromProjectWithAuth();

            const isLoading = isCreating || isFetchingClasses;

            const _showProjectEnvClassSelector = useMemo(() => {
                return (
                    showProjectEnvClassSelector ??
                    needsEnvClassSelector(project?.id, preferredEnvironmentClassPerProject, projectEnvironmentClasses)
                );
            }, [preferredEnvironmentClassPerProject, project, projectEnvironmentClasses, showProjectEnvClassSelector]);

            /**
             * Resolves project environment classes, using the cached query data
             * when available or fetching on-demand when classes were deferred.
             */
            const resolveProjectEnvironmentClasses = useCallback(async () => {
                if (projectEnvironmentClasses) {
                    return projectEnvironmentClasses;
                }
                // Fetch on-demand (hits cache if already fetched by another component)
                return await queryClient.fetchQuery(classesQueryOptions);
            }, [projectEnvironmentClasses, queryClient, classesQueryOptions]);

            const createEnvironment = useCallback(
                async (
                    project: PlainProject,
                    preferredEnvClassId?: string,
                    rememberChoice?: boolean,
                    skipAuthCheck?: boolean,
                ) => {
                    setCreating(true);
                    try {
                        const classes = await resolveProjectEnvironmentClasses();
                        const result = await createEnvironmentFromProjectWithAuth({
                            project,
                            projectEnvironmentClasses: classes?.environmentClasses ?? [],
                            preferredEnvClassId,
                            rememberChoice,
                            skipAuthCheck,
                        });

                        if (result?.type === "success") {
                            toast({ title: "Created new environment" });
                            onCreateSuccess?.();
                            navigate(getDetailsURL({ environment: result.environment }));
                            setShowScmAuthModal(undefined);
                        } else if (result?.type === "authRequired") {
                            setShowScmAuthModal({ ...result.authResult, rememberChoice });
                        }
                    } catch (e) {
                        if (
                            !_showProjectEnvClassSelector &&
                            e instanceof ConnectError &&
                            (e.code === Code.FailedPrecondition || e.code === Code.NotFound) &&
                            !isCreditsExhaustionError(e) && // These are handled by the top level credits exhaustion check
                            !isVerifyCardError(e) && // These are handled by the top level verify card check
                            !isPolicyError(e) // Policy errors are terminal — don't show env class selector
                        ) {
                            const details = e.findDetails(
                                CreateEnvironmentFromProjectBadRequestEnvironmentClassDetailsSchema,
                            );
                            setShowEnvClassSelectorModal({
                                filteredEnvironmentClassIds:
                                    details.length > 0 && details[0].environmentClassIds.length > 0
                                        ? details[0].environmentClassIds
                                        : undefined,
                                title: "Failed to create environment",
                                description: `${formatError(e)}`,
                            });
                        } else if (
                            !isVerifyCardError(e) && // These are handled by the top level verify card check
                            !isCreditsExhaustionError(e) // These are handled by the top level credits exhaustion check
                        ) {
                            toast({
                                title: "Failed to create an environment",
                                description: formatError(e),
                                type: "error",
                                duration: 10_000,
                            });
                        }
                    } finally {
                        setCreating(false);
                    }
                },
                [
                    setCreating,
                    resolveProjectEnvironmentClasses,
                    createEnvironmentFromProjectWithAuth,
                    toast,
                    onCreateSuccess,
                    navigate,
                    _showProjectEnvClassSelector,
                ],
            );

            const disabled =
                props.disabled || !project || isPendingPreference || (!deferClassesFetch && isLoadingProjectClasses);

            return (
                <>
                    <Tooltip content={hideTooltip ? "" : "Create environment"} usePortal>
                        <Button
                            {...props}
                            ref={ref}
                            type="button"
                            disabled={disabled}
                            loading={isLoading}
                            onClick={async () => {
                                if (project) {
                                    if (_showProjectEnvClassSelector) {
                                        setShowEnvClassSelectorModal({});
                                    } else if (deferClassesFetch && showProjectEnvClassSelector === undefined) {
                                        // Classes weren't fetched yet — resolve them to
                                        // decide whether to show the selector or create directly.
                                        setIsFetchingClasses(true);
                                        setCreating(true);
                                        try {
                                            const classes = await resolveProjectEnvironmentClasses();
                                            if (
                                                needsEnvClassSelector(
                                                    project.id,
                                                    preferredEnvironmentClassPerProject,
                                                    classes,
                                                )
                                            ) {
                                                setShowEnvClassSelectorModal({});
                                                setCreating(false);
                                            } else {
                                                // createEnvironment manages the atom itself
                                                await createEnvironment(
                                                    project,
                                                    preferredEnvironmentClassPerProject.get(project.id),
                                                );
                                            }
                                        } catch (e) {
                                            setCreating(false);
                                            toast({
                                                title: "Failed to load environment classes",
                                                description: formatError(e),
                                                type: "error",
                                            });
                                        } finally {
                                            setIsFetchingClasses(false);
                                        }
                                    } else {
                                        await createEnvironment(
                                            project,
                                            preferredEnvironmentClassPerProject.get(project.id),
                                        );
                                    }
                                }
                            }}
                            aria-label={props["aria-label"] ?? "Create environment"}
                            data-testid={`create-environment-from-project-button-${project?.id}`}
                            data-tracking-id="create-environment-button"
                        />
                    </Tooltip>
                    {showEnvClassSelectorModal && project && (
                        <SelectEnvironmentClassForProjectModal
                            project={project}
                            filteredEnvironmentClassIds={showEnvClassSelectorModal.filteredEnvironmentClassIds}
                            title={showEnvClassSelectorModal.title}
                            description={showEnvClassSelectorModal.description}
                            onClose={() => setShowEnvClassSelectorModal(undefined)}
                            onSelect={async (environmentClass: EnvironmentClassEntry, rememberChoice: boolean) => {
                                setShowEnvClassSelectorModal(undefined);
                                await createEnvironment(project, environmentClass.clazz.id, rememberChoice);
                            }}
                        />
                    )}
                    {showScmAuthModal && (
                        <AuthenticationModal
                            authenticationUrl={showScmAuthModal.authenticationResult.authenticationUrl}
                            patAuth={showScmAuthModal.authenticationResult.supportsPat}
                            environmentClass={showScmAuthModal.environmentClass}
                            scmId={showScmAuthModal.authenticationResult.scmId}
                            scmName={showScmAuthModal.authenticationResult.scmName}
                            repoURL={getRepoUrlFromInitializer(showScmAuthModal.project.initializer) || ""}
                            onClose={() => setShowScmAuthModal(undefined)}
                            onAuthSuccess={() =>
                                createEnvironment(
                                    showScmAuthModal.project,
                                    showScmAuthModal.environmentClass.id,
                                    showScmAuthModal.rememberChoice,
                                    true,
                                )
                            }
                        />
                    )}
                </>
            );
        },
    ),
);
CreateEnvironmentButton.displayName = "CreateEnvironmentButton";

type SCMAuthenticationModalProps = {
    authenticationUrl: string;
    patAuth: PlainAuthenticationMethodPersonalAccessToken | undefined;
    scmId: string;
    scmName: string;
    environmentClass: PlainRunnerEnvironmentClass;
    repoURL: string;
    onClose: () => void;
    onAuthSuccess: () => void;
};
const AuthenticationModal: FC<SCMAuthenticationModalProps> = memo((p) => {
    return (
        <div>
            <SCMAuthenticationModal
                repoURL={p.repoURL}
                authenticationUrl={p.authenticationUrl}
                patAuth={p.patAuth}
                scmId={p.scmId}
                scmName={p.scmName}
                onClose={p.onClose}
                onClickContinue={p.onAuthSuccess}
                runnerId={p.environmentClass.runnerId}
                data-track-location={TrackLocations.CreateEnvironmentSCMAuthenticationModal}
            />
        </div>
    );
});
AuthenticationModal.displayName = "AuthenticationModal";
